/**
 * Canonical add-to-cart event contract (schema v2).
 *
 * ONE definition for the three events that describe a single add-to-cart attempt,
 * shared by every client surface (PDP buybox, PDP sticky bar, calculator buy bridge)
 * and by the server-side confirmation in `src/lib/cart/add-line.ts`:
 *
 *  1. `product_add_to_cart_attempted` — client, fired once per `cart_add_attempt_id`
 *     right after input validation / submit-lock, BEFORE the request leaves the browser.
 *  2. `product_added_to_cart`        — server, fired ONLY after Saleor confirmed the line.
 *     Deliberately the only success event so existing funnels keep counting once.
 *  3. `product_add_to_cart_failed`   — client, fired on network failure, unparsable
 *     response or `{ ok: false }`, carrying the stable machine-readable error code.
 *
 * `cart_add_attempt_id` is the join key across all three. Event names and property
 * shapes live here so no surface hand-rolls a string literal or a property map.
 */

import { parseVariantDoseMg } from "@/lib/pricing/variant-pack-math";
import { getPurchasableVariantDetails } from "@/lib/products/purchasable-variant";

/**
 * Bump only for an INCOMPATIBLE change to the property shape below — a renamed, removed
 * or re-typed property, i.e. anything that makes a saved insight read the stream wrong.
 * Additive vocabulary (a new `CartInternalSource` / `CartAddSurface` value, a new error
 * code) does NOT bump: existing property filters keep matching, they just see one more
 * value. Bump together with the PostHog insights that pin the version.
 */
export const ADD_TO_CART_EVENT_SCHEMA_VERSION = 2;

export const ADD_TO_CART_ATTEMPTED_EVENT = "product_add_to_cart_attempted";
/** Existing canonical success event — do NOT introduce a second one. */
export const ADD_TO_CART_SUCCEEDED_EVENT = "product_added_to_cart";
export const ADD_TO_CART_FAILED_EVENT = "product_add_to_cart_failed";

/**
 * Where the journey that ended in this add started. `calculator`, `quiz`, `search` and
 * `stack-builder` are the `InternalSource` values a link can hand to the product page it
 * opens (`@/lib/analytics/internal-source`); `pdp` is the fallback for an add on a product
 * page nothing referred to.
 *
 * CARRIER CHANGE (branch feat/seo-audit-massnahmen-20261016, 17 Oct 2026): before its deploy
 * the referring link carried `?src=…&src_slug=…` in the PDP URL. From that deploy on the link
 * stays clean and the click hands the source to the target PDP through `sessionStorage`
 * (same tab, 30 minutes counted from the click); the PDP stamps it on `product_viewed` and on
 * the add. Property names and values are unchanged, so the schema version stays at 2, but
 * `$current_url` / `$pathname` filters on `src=` stop matching at the deploy date. Coverage
 * limits (late adds, revisits, new-tab and pre-hydration clicks) are listed in the header of
 * `internal-source.ts`.
 *
 * SEMANTIC SHIFT 2026-07-30: with `search` added and the PDP add inheriting the source
 * of the link that opened the page, `internal_source = "pdp"` on
 * `product_added_to_cart` no longer means "added on a product page" — it means
 * UNREFERRED product-page add. Calculator- and search-referred adds now carry their own
 * source. To count ALL product-page adds, filter `add_surface in (pdp_buybox,
 * pdp_sticky, bac_cross_sell)`, never `internal_source = pdp`. Additive change, so the schema version
 * stays at 2 (see above); the deploy date is the only boundary in the stream.
 *
 * `lp` (added 2026-09-03) is the Facebook-Ads landing page. Like `home` it is off the
 * product page and no referring link hands it a source, so it stamps its own on every add.
 */
export type CartInternalSource = "calculator" | "home" | "lp" | "pdp" | "quiz" | "search" | "stack-builder";
/**
 * The concrete control that was clicked. `home` pairs only with `home_bundle` (the
 * homepage bundle block is the one add surface that is not on a product page and cannot
 * inherit a referring source); `bac_cross_sell` is a product-page surface and therefore inherits
 * the referring source exactly like the buybox does. The three `lp_*` values are the
 * landing page's own CTAs (calculator card, sticky bar, closing block) and pair only
 * with `lp`, the same rule the homepage bundle follows.
 */
export type CartAddSurface =
	| "bac_cross_sell"
	| "calculator_buy_bridge"
	| "home_bundle"
	| "lp_bottom"
	| "lp_calc_card"
	| "lp_sticky"
	| "pdp_buybox"
	| "pdp_sticky"
	| "stack_builder";
export type CartExperimentVariant = "control" | "direct_add";

/**
 * Stable, machine-readable failure codes. The API returns exactly these in
 * `{ ok: false, error }`; the client forwards them 1:1 as `error_code`.
 * Never widen this with raw exception text or anything user-supplied.
 */
const ADD_TO_CART_ERROR_CODES = [
	"invalid-json",
	"missing-channel",
	"missing-variant",
	"checkout-create-failed",
	"saleor-request-failed",
	"saleor-line-rejected",
	"insufficient-stock",
	"line-not-confirmed",
	"invalid-response",
	"network",
	"exception",
] as const;

export type AddToCartErrorCode = (typeof ADD_TO_CART_ERROR_CODES)[number];

const ERROR_CODES = new Set<string>(ADD_TO_CART_ERROR_CODES);

export function isAddToCartErrorCode(value: unknown): value is AddToCartErrorCode {
	return typeof value === "string" && ERROR_CODES.has(value);
}

/**
 * Take the API's error code 1:1 when it is one of the known codes, otherwise fall
 * back — an unknown string could be raw error text and must never reach PostHog.
 */
export function normalizeAddToCartErrorCode(
	value: unknown,
	fallback: AddToCartErrorCode = "exception",
): AddToCartErrorCode {
	return isAddToCartErrorCode(value) ? value : fallback;
}

/** Anything slower than this is a clock-skew artifact, not a real click-to-response time. */
const MAX_ADD_TO_CART_LATENCY_MS = 120_000;

/**
 * Click → confirmed response, in whole milliseconds. Returns `undefined` (property
 * omitted) for missing, non-numeric, negative or implausibly large deltas — the
 * client's `attemptStartedAtMs` is browser-clock based and can be skewed vs. the server.
 */
export function resolveAddToCartLatencyMs(
	startedAtMs: unknown,
	nowMs: number = Date.now(),
): number | undefined {
	if (typeof startedAtMs !== "number" || !Number.isFinite(startedAtMs) || startedAtMs <= 0) {
		return undefined;
	}
	const delta = Math.round(nowMs - startedAtMs);
	if (!Number.isFinite(delta) || delta < 0 || delta > MAX_ADD_TO_CART_LATENCY_MS) {
		return undefined;
	}
	return delta;
}

export interface AddToCartVariantFacets {
	dosage_mg?: number;
	dosage_label?: string;
	pack_size?: number;
}

/**
 * Dosage/pack facets derived from the Saleor variant name via the SHARED parsers
 * (`parseVariantDoseMg` for the numeric dose, `getPurchasableVariantDetails` for the
 * normalized label + pack count) so cart analytics never drifts from PDP/pricing.
 */
export function resolveAddToCartVariantFacets(variantName?: string | null): AddToCartVariantFacets {
	const name = typeof variantName === "string" ? variantName.trim() : "";
	if (!name) return {};

	const details = getPurchasableVariantDetails({ id: "", name });
	const doseMg = parseVariantDoseMg(name);

	return {
		...(typeof doseMg === "number" && Number.isFinite(doseMg) ? { dosage_mg: doseMg } : {}),
		...(details.doseLabel ? { dosage_label: details.doseLabel } : {}),
		pack_size: details.packCount,
	};
}

/** Everything a surface can know about one add-to-cart attempt. */
export interface AddToCartEventInput {
	/** Same UUID on attempt, success and failure. */
	cartAddAttemptId?: string | null;
	productSlug?: string | null;
	/** Localized product name as rendered to the visitor. */
	productName?: string | null;
	/** Raw Saleor global id. */
	variantId: string;
	variantName?: string | null;
	variantSku?: string | null;
	/** Number of pack variants added — NOT the pack size. */
	quantity: number;
	channel: string;
	locale?: string | null;
	/** Saleor settlement currency (EUR/USD) — NEVER the visitor's reference currency. */
	currency?: string | null;
	/** Gross value of the newly added quantity (confirmed unit price × requested quantity). */
	value?: number | null;
	internalSource?: CartInternalSource | null;
	internalSourceDetail?: string | null;
	addSurface?: CartAddSurface | null;
	experimentVariant?: CartExperimentVariant | null;
	checkoutId?: string | null;
	pathname?: string | null;
	sessionId?: string | null;
	/** Click → confirmed response. Omitted on the attempt event by definition. */
	latencyMs?: number | null;
}

export type AddToCartEventProperties = Record<string, unknown>;

function text(value: unknown): string | undefined {
	if (typeof value !== "string") return undefined;
	const trimmed = value.trim();
	return trimmed ? trimmed : undefined;
}

function num(value: unknown): number | undefined {
	return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function omitUndefined(properties: AddToCartEventProperties): AddToCartEventProperties {
	const out: AddToCartEventProperties = {};
	for (const [key, value] of Object.entries(properties)) {
		if (value !== undefined) out[key] = value;
	}
	return out;
}

function buildBaseProperties(
	input: AddToCartEventInput,
	trackingSource: "client" | "server",
): AddToCartEventProperties {
	return omitUndefined({
		event_schema_version: ADD_TO_CART_EVENT_SCHEMA_VERSION,
		cart_add_attempt_id: text(input.cartAddAttemptId),
		product_slug: text(input.productSlug),
		product_name: text(input.productName),
		variant_id: input.variantId,
		variant_name: text(input.variantName),
		variant_sku: text(input.variantSku),
		...resolveAddToCartVariantFacets(input.variantName),
		quantity: input.quantity,
		channel: input.channel,
		locale: text(input.locale),
		currency: text(input.currency),
		value: num(input.value),
		internal_source: input.internalSource ?? undefined,
		internal_source_detail: text(input.internalSourceDetail),
		add_surface: input.addSurface ?? undefined,
		experiment_variant: input.experimentVariant ?? undefined,
		checkout_id: text(input.checkoutId),
		$pathname: text(input.pathname),
		$session_id: text(input.sessionId),
		tracking_source: trackingSource,
	});
}

/** Client event, fired before the request leaves the browser (no latency yet). */
export function buildAddToCartAttemptProperties(input: AddToCartEventInput): AddToCartEventProperties {
	const { latencyMs: _ignored, ...rest } = input;
	return buildBaseProperties(rest, "client");
}

/** Server event, fired only after Saleor confirmed the line. */
export function buildAddToCartSuccessProperties(input: AddToCartEventInput): AddToCartEventProperties {
	return omitUndefined({
		...buildBaseProperties(input, "server"),
		add_to_cart_latency_ms: num(input.latencyMs),
	});
}

export interface AddToCartFailureInput extends AddToCartEventInput {
	errorCode: AddToCartErrorCode;
	/** Only where it carries information (non-2xx response); omitted for transport failures. */
	httpStatus?: number | null;
}

/** Client event for network failure, unparsable response or `{ ok: false }`. */
export function buildAddToCartFailureProperties(input: AddToCartFailureInput): AddToCartEventProperties {
	return omitUndefined({
		...buildBaseProperties(input, "client"),
		add_to_cart_latency_ms: num(input.latencyMs),
		error_code: input.errorCode,
		http_status: num(input.httpStatus),
	});
}
